// SPDX-License-Identifier: AGPL-3.0-or-later

import * as AuthenticationCommands from '@app/features/auth/commands/AuthenticationCommands';
import {VerificationResult} from '@app/features/auth/commands/AuthenticationCommands';
import {Button} from '@app/features/ui/button/Button';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import {WarningAlert} from '@app/features/ui/warning_alert/WarningAlert';
import {Trans, useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useState} from 'react';

interface EmailVerificationAlertProps {
	title?: React.ReactNode;
	children?: React.ReactNode;
}
export const EmailVerificationAlert = observer(({title, children}: EmailVerificationAlertProps) => {
	const {t} = useLingui();
	const [isResending, setIsResending] = useState(false);
	const handleResend = async () => {
		if (isResending) return;
		setIsResending(true);
		const result = await AuthenticationCommands.resendVerificationEmail();
		switch (result) {
			case VerificationResult.SUCCESS:
				ToastCommands.success(t`Verification email sent! Please check your inbox.`);
				break;
			case VerificationResult.RATE_LIMITED:
				ToastCommands.error(t`Too many requests. Please try again later.`);
				break;
			case VerificationResult.SERVER_ERROR:
				ToastCommands.error(t`Failed to send verification email. Please try again later.`);
				break;
		}
		setIsResending(false);
	};
	return (
		<WarningAlert
			title={title ?? <Trans>Email Verification Required</Trans>}
			actions={
				<Button variant="primary" small disabled={isResending} submitting={isResending} onClick={handleResend}>
					<Trans>Resend Email</Trans>
				</Button>
			}
		>
			{children ?? <Trans>Please check your inbox for a verification email.</Trans>}
		</WarningAlert>
	);
});
